import { GCC_WAVE_BANDS } from '@tn/shared'

const BANDS = `Wave 1 = ${GCC_WAVE_BANDS[1].label}, Wave 2 = ${GCC_WAVE_BANDS[2].label}, Wave 3 = ${GCC_WAVE_BANDS[3].label}`

/** Plain-string tooltip content for GCC Dashboard KPIs, tabs, and wizard fields. */
export const GCC_TOOLTIPS = {
  // ── WVC KPI labels ───────────────────────────────────────────────────────
  endStateHeadcount:
    'Projected headcount after the GCC transition completes — all roles fully staffed at end-state sourcing tiers.',
  headcountDelta:
    'Net change in headcount vs. today. Negative values indicate consolidation; positive values indicate net-new GCC roles.',
  labourSaving:
    'Annual run-rate labour cost saving vs. today\'s baseline (USD): today\'s fully-loaded cost minus projected end-state cost.',
  labourCostToday:
    'Current annual fully-loaded labour cost for this population, based on role count × pay-rate benchmark.',
  labourCostEndState:
    'Projected annual fully-loaded labour cost after GCC transition, using GCC-tier pay-rate benchmarks.',

  // ── Disposition tab pills (WI 2576): as defined in the Step 1 prompt ──────
  dispositionEliminate:
    'The task can stop altogether: no person or machine needs to do it.',
  dispositionAutomate:
    'Rules-based work that RPA or workflow automation runs end to end. People handle the exceptions.',
  dispositionAgentify:
    'An AI agent runs the task on its own. People oversee it and handle the exceptions.',
  dispositionAugment:
    'A person stays the primary actor, with AI assistance that makes the work faster or better.',
  dispositionHuman:
    'Work that needs human judgement, leadership or relationships, so AI does not change it.',
  dispositionEliminateZeroNone:
    'Why 0: Step 1 tagged no task Eliminate. Every task still has work for a person or a machine, so this is the result of the assessment, not missing data.',
  dispositionEliminateZeroNoTasks:
    'Why 0: no tasks are counted on this tab, so every disposition shows 0.',

  // ── Sequencing columns ────────────────────────────────────────────────────
  urgencyScore:
    'Composite urgency score (1–3): 1 = low urgency, 3 = act now. Derived from strategic value, competitive pressure, and transition readiness.',
  waveAssignment:
    `Recommended implementation wave: ${BANDS}.`,
  shiftVerdict:
    'Recommended timing to shift this capability to its target sourcing tier (Now / Next / Later / Never).',
  transformVerdict:
    'Recommended timing to redesign the way-of-working for this capability (Now / Next / Later / Never).',

  // ── Sequencing tab: pathways + waves (WI 2325) ──────────────────────────
  sequencingPathway:
    'How AI transformation and migration are ordered. Set per task in code from its two waves; the capability shows the pathway that covers most of its baseline FTE.',
  sequencingPathwaySplit:
    'Share of the capability’s baseline FTE on each pathway. More than one pathway means the capability is mixed.',
  sequencingTransformWave:
    `When AI transformation starts: the earliest wave of the capability’s tasks. ${BANDS}.`,
  sequencingMigrationWave:
    `When work starts moving to its new owner: the earliest wave of the capability’s tasks. ${BANDS}.`,
  sequencingAiReadiness:
    'LLM score 1–5: how soon the AI disposition can be in place. 4–5 → Wave 1, 3 → Wave 2, 1–2 → Wave 3.',
  sequencingTransitionFeasibility:
    'LLM score 1–5: how soon the work can move as it is today. 4–5 → Wave 1, 3 → Wave 2, 1–2 → Wave 3.',
  sequencingWaveFlow:
    'FTE released by AI in each wave, and FTE moving to a new owner. Transform First and Together move the residual; Shift First and Shift Only move today’s FTE.',

  // ── Quick win, agentify first (WI 2806) ─────────────────────────────────
  quickWin:
    'An Automate or Agentify task that Step 1 judged Low complexity and implementable within the run’s horizon (months). Its AI transformation is planned no later than that, and before any of its work moves. Independent of the share AI releases, so it complements Automate First.',
  sequencingQuickWin:
    'Quick wins are agentified first: the transform wave is no later than the wave its months to implement fall in, and any migration comes after it (Transform First). In Wave 3 there is no later wave, so the two stay together.',

  // ── Sourcing Model tab columns ────────────────────────────────────────────
  sourcingCapability:
    'The task or business function being assessed for GCC placement.',
  sourcingTier:
    'Recommended delivery location for this capability.',
  sourcingOwnership:
    'Who owns the work in the end state. Shown separately from where it is delivered.',
  sourcingLocation:
    'Where the work is delivered in the end state.',
  sourcingResidualFte:
    'FTE-equivalents of work left for people after AI (Step 1). This is what gets sourced.',
  sourcingSplit:
    'Residual FTE per ownership × location. Each task is sourced on its own, so one capability can be split (hybrid). The split always adds up to the residual FTE.',
  sourcingSignal:
    'Evidence source used to determine the sourcing tier (e.g. Talent Navigator model, LLM inference).',
  sourcingRationale:
    'AI-generated explanation for the sourcing tier assignment.',
  sourcingAutomateFirst:
    'Automate first: a task whose AI disposition releases at least the threshold share of its work is not migrated. Its remaining work stays Retained where the role works today, and it is not sent to the AI for sourcing.',

  // ── End-State Roles tab columns ───────────────────────────────────────────
  rolesRole:
    'Job title in the future workforce model.',
  rolesSegment:
    'Which workforce population this role belongs to — GCC-eligible (moves offshore) or Retained (stays onshore).',
  rolesTag:
    'How this role transforms in the end-state workforce model.',
  rolesTier:
    'Recommended delivery location for this role.',
  rolesHcToday:
    'Current headcount for this role in the as-is operating model.',
  rolesHcEndState:
    'Projected headcount for this role in the future operating model after GCC transition.',
  rolesSaving:
    'Estimated annual labour cost saving (USD) from transitioning this role to the GCC model.',

  // ── Residual workload (WI 2323) ───────────────────────────────────────────
  residualWorkload:
    'FTE-equivalent work left for people after AI disposition, per capability (Step 0 process). Calculated in code: task FTE × residual % for its disposition, summed over the capability.',
  residualBaselineFte:
    "Current-state FTE from the Step 0 baseline: role FTE × the task's share of that role's time, summed over the capability.",
  residualFte:
    'FTE-equivalent work still done by people after Eliminate / Agentify / Automate / Augment, including oversight and exception handling.',
  residualPct:
    'Residual FTE as a share of baseline FTE. 100% means no reduction (every task Human).',

  // ── Value case (WI 2326) ──────────────────────────────────────────────────
  savingSplit:
    'The labour saving split by value driver: AI productivity (released FTE at today’s rate) and location arbitrage (residual FTE at a lower end-state rate). The two add up to the labour saving.',
  valueCaseWaterfall:
    'Traceability chain, calculated in code from Steps 0–3: current FTE, minus the FTE AI releases, gives the residual FTE; Step 2 splits it by ownership × location; the net value case prices each step at the pay-rate table.',
  valueCaseAiSaving:
    'AI productivity saving: AI-released FTE × today’s rate for the role at its current location (HQ or Markets). Counted first, so AI value is never also counted as location value.',
  valueCaseLocationSaving:
    'Location arbitrage saving: residual FTE × (today’s rate − end-state rate). Zero for Retained work, which does not move.',
  valueCaseTotalSaving:
    'Total run-rate labour saving = AI saving + location saving = current cost − end-state cost. One-off build and transition costs are not included.',
  valueCaseFutureFte:
    'Residual FTE by end-state owner: Captive GCC, MSP or Retained. Equal to the Step 2 sourcing split.',
  valueCaseWaves:
    `Run-rate saving that starts in each wave: a task’s AI saving from its Step 3 transform wave, its location saving from its migration wave (Wave 3 when it has none). ${BANDS}.`,
  valueCaseAssumptions:
    'Inputs to the value case that are assumptions, not calculations: the pay-rate table, how each rate was matched, and any default that was applied.',

  // ── Value case column headers (WI 2577) ─────────────────────────────────
  valueCaseCapability:
    'The capability and the function it belongs to. Show tasks opens the task ledger behind its figures.',
  valueCaseCurrentFte:
    'Today’s FTE from the Step 0 baseline: role FTE × the task’s share of that role’s time, summed over the tasks.',
  valueCaseAiReleasedFte:
    'FTE released by the AI disposition (Step 1): current FTE minus residual FTE.',
  valueCaseResidualFte:
    'FTE-equivalent work left for people after AI. This is what Step 2 sources and what is priced at end-state rates.',
  valueCaseCurrentCost:
    'Today’s annual labour cost: current FTE × today’s rate for the role at its current location (HQ or Markets), from the pay-rate table.',
  valueCaseEndStateCost:
    'Annual labour cost after the change: residual FTE × the end-state rate for its ownership × location (today’s rate for Retained work). Equal to current cost − total saving.',
  valueCaseChainTotal:
    'The figure for the whole design: every capability added up.',
  valueCaseLedgerTask:
    'The task, and the role that does it today.',
  valueCaseLedgerDisposition:
    'The task’s AI disposition from Step 1: Eliminate, Automate, Agentify, Augment or Human.',
  valueCaseLedgerTiers:
    'Where the work sits today → its end-state ownership × location from Step 2.',
  valueCaseLedgerFte:
    'The task’s current FTE → the FTE left for people after AI.',
  valueCaseLedgerRates:
    'Annual rate per FTE today → in the end state, from the pay-rate table. No rate means the role has no match in the table.',
  valueCaseLedgerAi:
    'This task’s AI saving: released FTE × today’s rate.',
  valueCaseLedgerLocation:
    'This task’s location saving: residual FTE × (today’s rate − end-state rate). Zero for Retained work.',
  valueCaseLedgerFrom:
    'First wave in which the task saves anything: the earlier of its transform wave (AI saving) and migration wave (location saving), Wave 3 when it has none.',
  valueCaseWaveAiStart:
    'AI saving from the tasks whose Step 3 transform wave is this wave (Wave 3 when a task has none).',
  valueCaseWaveLocationStart:
    'Location saving from the tasks whose migration wave is this wave (Wave 3 when a task has none).',
  valueCaseWaveRunRate:
    'Annual run-rate saving once this wave and every earlier wave are in place. The last wave equals the total saving.',
  valueCasePayTier:
    'The location tier the rate applies to: HQ, Markets, GCC nearshore, GCC offshore or MSP.',
  valueCasePayRole:
    'The role title the rate prices.',
  valueCasePayRate:
    'Annual rate per FTE (USD) that the value case used for this tier and role.',
  valueCasePayMatch:
    'How the role was matched to the table: Exact title, Role band (a seniority keyword in the title), Analyst floor (the location’s Analyst rate), or No rate.',

  // ── Disposition drilldown columns ─────────────────────────────────────────
  dispositionTask:
    'Individual task within this disposition group.',
  dispositionSourcingTier:
    'Recommended delivery location for this task.',
  dispositionSourcing:
    'Who owns this task’s residual work and where it is delivered, from Step 2. Tasks with no residual work are not sourced.',
  dispositionExistenceVerdict:
    'Whether the task should continue to exist in the future operating model.',
  dispositionAgentMatch:
    'The AI’s estimate that an Accenture agent likely exists to handle or assist this task. It has not been checked against an agent catalog; catalog matching is a planned feature.',

  // ── Wizard config fields ──────────────────────────────────────────────────
  clientName:
    'Full legal or trading name of the client organisation. Appears in all generated documents and dashboard titles.',
  industry:
    'Primary industry vertical. Influences AI task generation content and benchmarking assumptions.',
  primaryColor:
    'Brand colour applied to the GCC Dashboard and all exported HTML documents.',
  eligibilityRules:
    'Criteria that classify a task as GCC-eligible. A task must meet all rules to be included in the GCC-Eligible population.',
  payRateTable:
    'Annual salary benchmarks (USD) per sourcing tier and role title. Used to compute the workforce value case.',
} as const
